import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const SERVICES = [
    { value: "The Signal", label: "01 — The Signal (Red/Amber/Green)" },
    { value: "The Dream Plan", label: "02 — The Dream Plan" },
    { value: "The Business Edge", label: "03 — The Business Edge" },
    { value: "Not sure yet", label: "Not sure yet" },
];

export const Contact = () => {
    const [form, setForm] = useState({
        nome: "",
        email: "",
        servizio: "",
        messaggio: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nome || !form.email || !form.servizio || !form.messaggio) {
            toast.error("Fill in every field so I can run your numbers.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, form);
            setDone(true);
            toast.success("Sent. I'll be in touch very soon.");
            setForm({ nome: "", email: "", servizio: "", messaggio: "" });
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32"
        >
            {/* Message form (secondary path — main path is Pay & book above) */}
            <div className="grid md:grid-cols-12 gap-12 md:gap-16">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease }}
                    className="md:col-span-5"
                >
                    <p className="overline text-[#C5A059] mb-6">
                        — Not ready to book?
                    </p>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-8">
                        Send me a{" "}
                        <span className="italic text-[#C5A059]">message</span>.
                    </h2>
                    <p className="font-body text-lg text-[#8E95A3] leading-relaxed max-w-md mb-6">
                        Ready to start? Pick your package above — you pay first,
                        then choose your call time instantly. Prefer to ask
                        something first? Write to me here and I&apos;ll reply
                        personally.
                    </p>
                    <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#C5A059]">
                        Only 8 calls open each month — launch price ends at 10.
                    </p>
                </motion.div>

                <motion.form
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15, ease }}
                    className="md:col-span-6 md:col-start-7 flex flex-col gap-8"
                >
                    <div className="grid sm:grid-cols-2 gap-8">
                        <div>
                            <label className="overline text-white/50 block mb-2">
                                Name
                            </label>
                            <input
                                data-testid="input-nome"
                                className="editorial-input"
                                placeholder="Your name"
                                value={form.nome}
                                onChange={(e) => update("nome", e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="overline text-white/50 block mb-2">
                                Email
                            </label>
                            <input
                                data-testid="input-email"
                                type="email"
                                className="editorial-input"
                                placeholder="you@email.com"
                                value={form.email}
                                onChange={(e) =>
                                    update("email", e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div>
                        <label className="overline text-white/50 block mb-2">
                            Service of interest
                        </label>
                        <Select
                            value={form.servizio}
                            onValueChange={(v) => update("servizio", v)}
                        >
                            <SelectTrigger
                                data-testid="select-servizio"
                                className="editorial-input rounded-none border-0 border-b border-white/20 px-0 focus:ring-0 shadow-none h-auto text-base data-[placeholder]:text-white/35"
                            >
                                <SelectValue placeholder="Pick a service" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#11141D] border-white/10">
                                {SERVICES.map((s) => (
                                    <SelectItem
                                        key={s.value}
                                        value={s.value}
                                        data-testid={`servizio-${s.value}`}
                                        className="font-mono text-sm"
                                    >
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="overline text-white/50 block mb-2">
                            Message
                        </label>
                        <textarea
                            data-testid="input-messaggio"
                            rows={3}
                            className="editorial-input resize-none"
                            placeholder="Tell me where you are and where you want to go..."
                            value={form.messaggio}
                            onChange={(e) =>
                                update("messaggio", e.target.value)
                            }
                        />
                    </div>

                    <button
                        data-testid="submit-lead"
                        type="submit"
                        disabled={loading || done}
                        className="group relative mt-2 inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#070A13] font-body font-semibold text-lg rounded-full px-8 py-5 overflow-hidden transition-transform duration-300 hover:-translate-y-1 disabled:opacity-60 disabled:translate-y-0"
                    >
                        <span className="relative z-10 flex items-center gap-3">
                            {loading ? (
                                <>
                                    <Loader2
                                        className="animate-spin"
                                        size={20}
                                    />
                                    Sending...
                                </>
                            ) : done ? (
                                "Thanks! I'll be in touch"
                            ) : (
                                <>
                                    Run my numbers
                                    <ArrowUpRight
                                        size={20}
                                        className="transition-transform duration-300 group-hover:rotate-45"
                                    />
                                </>
                            )}
                        </span>
                        <span className="absolute inset-0 bg-[#C5A059] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    </button>
                </motion.form>
            </div>
        </section>
    );
};
